import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback statique — miroir de PLAN_CATALOG_DEFAULTS backend (utilisé si l'API est injoignable)
export const PLAN_FALLBACK = [
    {
        tier: 'free', name: 'Explorer', emoji: '🌱', order: 1, recommended: false, contact_only: false,
        tagline: 'Porteurs de projet · associations · découverte',
        description: 'Freemium permanent : structurez votre premier projet gratuitement, sans limite de durée ni carte bancaire.',
        quotas: { organisations: '1', projets: '1 actif', utilisateurs: '3', ia: '20 req. IA/mois' },
        features: ['GANTT, Budget, GED, Visio', 'Valley AI — 20 requêtes/mois', 'Campus 360° (formation certifiante)', 'Exports PDF'],
        cta: 'Créer mon compte gratuit', cta_to: '/signup-free', monthly_eur: null, yearly_eur: null, plan_ids: null,
    },
    {
        tier: 'essentiel', name: 'Essentiel', emoji: '🌿', order: 2, recommended: false, contact_only: false,
        tagline: 'Petites ONG · cabinets · premiers portefeuilles',
        description: 'Pilotez plusieurs projets avec votre équipe et un support réactif.',
        quotas: { organisations: '1', projets: '3 actifs', utilisateurs: '10', ia: '50 req. IA/mois' },
        features: ['Tout Explorer +', '3 projets actifs · 10 utilisateurs', 'Valley AI — 50 requêtes/mois', 'Audit logs avancés', 'Support email 48 h'],
        cta: 'Choisir Essentiel', cta_to: '/p/tarifs', monthly_eur: 39, yearly_eur: 390,
        plan_ids: { monthly: 'essentiel-monthly', yearly: 'essentiel-yearly' },
    },
    {
        tier: 'pro', name: 'Impact', emoji: '💼', order: 3, recommended: true, contact_only: false,
        tagline: 'ONG nationales · entreprises · agences',
        description: 'La gouvernance complète : reporting bailleurs, marque blanche et kits sectoriels inclus.',
        quotas: { organisations: '3', projets: '10 actifs', utilisateurs: '25', ia: '300 req. IA/mois' },
        features: ['Tout Essentiel +', '3 organisations · 10 projets · 25 utilisateurs', 'Kits Projet sectoriels inclus', 'Marque blanche incluse', 'API REST', 'Support prioritaire 24 h'],
        cta: 'Choisir Impact', cta_to: '/p/tarifs', monthly_eur: 99, yearly_eur: 990,
        plan_ids: { monthly: 'pro-monthly', yearly: 'pro-yearly' },
    },
    {
        tier: 'organisation', name: 'Organisation', emoji: '🏢', order: 4, recommended: false, contact_only: false,
        tagline: 'Groupes · programmes multi-entités',
        description: 'Pilotez un portefeuille multi-entités avec dashboards consolidés et onboarding assisté.',
        quotas: { organisations: '10', projets: 'Illimités', utilisateurs: '100', ia: '1 000 req. IA/mois' },
        features: ['Tout Impact +', '10 organisations · projets illimités · 100 utilisateurs', 'Dashboards consolidés multi-entités', 'Valley AI — 1 000 requêtes/mois', 'Onboarding assisté'],
        cta: 'Choisir Organisation', cta_to: '/p/tarifs', monthly_eur: 249, yearly_eur: 2490,
        plan_ids: { monthly: 'organisation-monthly', yearly: 'organisation-yearly' },
    },
    {
        tier: 'unlimited', name: 'Institution', emoji: '🏛️', order: 5, recommended: false, contact_only: true,
        tagline: 'Ministères · bailleurs · consortiums',
        description: 'Sur devis : capacité illimitée, offre Consortium multi-organisations incluse, SLA contractuel et accompagnement dédié.',
        quotas: { organisations: 'Illimitées', projets: 'Illimités', utilisateurs: 'Illimités', ia: 'Illimitée' },
        features: ['Tout Organisation +', 'Capacité illimitée', 'Offre Consortium incluse', 'SLA contractuel · support dédié', 'Onboarding sur site', 'Hébergement Afrique en option'],
        cta: 'Demander un devis', cta_to: '/p/contact', monthly_eur: null, yearly_eur: null, plan_ids: null,
    },
];

export async function fetchPlanCatalog() {
    try {
        const r = await axios.get(`${API}/billing/stripe/plans`);
        const plans = Array.isArray(r.data?.plans) && r.data.plans.length ? r.data.plans : PLAN_FALLBACK;
        return { plans, trial_days: r.data?.trial_days ?? 14, live: !!r.data?.live };
    } catch {
        return { plans: PLAN_FALLBACK, trial_days: 14, live: false };
    }
}

export const TIER_PUBLIC_LABEL = {
    free: '🌱 Explorer', essentiel: '🌿 Essentiel', pro: '💼 Impact',
    organisation: '🏢 Organisation', unlimited: '🏛️ Institution', starter: '🚀 Starter (legacy)',
};

// Comparateur — colonnes : Explorer · Essentiel · Impact · Organisation · Institution
export const COMPARATOR = [
    { group: 'Capacité', rows: [
        { label: 'Organisations (espaces)', plans: ['1', '1', '3', '10', 'Illimité'] },
        { label: 'Projets actifs', plans: ['1', '3', '10', 'Illimité', 'Illimité'] },
        { label: 'Utilisateurs', plans: ['3', '10', '25', '100', 'Illimité'] },
    ]},
    { group: 'Pilotage', rows: [
        { label: 'GANTT interactif (phases, jalons, tâches)', plans: [true, true, true, true, true] },
        { label: 'Budget & dépenses (justificatifs photo/PDF)', plans: [true, true, true, true, true] },
        { label: 'Multi-devises EUR / XOF / XAF / USD', plans: [true, true, true, true, true] },
        { label: 'GED (documents, versions, aperçus)', plans: [true, true, true, true, true] },
        { label: 'Exports PDF / Excel / PowerPoint', plans: [true, true, true, true, true] },
        { label: 'Workflows de validation multi-niveaux', plans: [true, true, true, true, true] },
    ]},
    { group: 'Intelligence & conformité', rows: [
        { label: 'Valley AI (requêtes / mois)', plans: ['20', '50', '300', '1 000', 'Illimité'] },
        { label: 'Reporting bailleurs', plans: [true, true, true, true, true] },
        { label: "Journal d'audit immuable", plans: [true, true, true, true, true] },
        { label: 'Audit logs avancés', plans: ['—', true, true, true, true] },
    ]},
    { group: 'Personnalisation', rows: [
        { label: 'Kits Projet sectoriels (14 secteurs)', plans: ['Option', 'Option', '✓ inclus', '✓ inclus', '✓ inclus'] },
        { label: 'Marque blanche (exports à votre marque)', plans: ['Option', 'Option', '✓ inclus', '✓ inclus', '✓ inclus'] },
        { label: 'Dashboards consolidés', plans: ['—', '—', '—', true, true] },
        { label: 'Multi-entités', plans: ['—', '—', '—', true, true] },
    ]},
    { group: 'Intégration & engagement', rows: [
        { label: 'API REST', plans: ['—', '—', true, true, true] },
        { label: 'SSO entreprise', plans: ['—', '—', '—', '—', 'Sur demande'] },
        { label: 'SLA contractuel', plans: ['—', '—', '—', '—', true] },
    ]},
    { group: 'Accompagnement', rows: [
        { label: 'Support', plans: ['Communauté', 'Email 48 h', 'Prioritaire 24 h', 'Prioritaire 24 h', 'Dédié · SLA'] },
        { label: 'Onboarding', plans: ['—', '—', '—', 'Assisté', 'Sur site'] },
    ]},
];
